/**
 * The rule segment builder — `/segments/new?type=rule`, and the Edit mode of a
 * rule segment's page. A port of the concept's `SegmentBuilder.jsx` onto the
 * real catalogue and the real compiler.
 *
 * ## WHAT IS LIVE, AND WHERE THE NUMBER COMES FROM
 *
 * Every count on this screen is the API's: the builder sends the complete part
 * of the tree to `POST /segments/preview` once typing settles (450 ms), and the
 * answer carries the whole tree's people, its email and WhatsApp reach, and a
 * count per rule and per group — each rule ON ITS OWN, which is what the
 * concept's per-row count meant. The preview runs the same compiler the
 * refresher runs on save, so the figure beside Save is the membership the save
 * writes. A previous request is aborted when a new one starts, so an old answer
 * cannot land on a newer tree.
 *
 * Zero and "not counted" stay apart. Before the first answer the figure is the
 * "awaiting" dash; with nothing complete to count it says so in words; a rule
 * the catalogue objects to (an unknown column, a date whose day/month order is
 * unanswered) blocks the count with the objection's own sentence rather than
 * showing a number the server would refuse to produce.
 *
 * ## WHAT THE CONCEPT HAD THAT THIS DOES NOT
 *
 * - **No sample of matching people.** The preview contract returns counts, not
 *   rows. Drawing five invented "matches" would be the fabrication this
 *   codebase's rules exist to stop; the segment page lists real members once
 *   it is saved.
 * - **No freeze / static toggle.** v1 has no freeze (the spec, slice 67): a
 *   rule segment is dynamic, full stop, and a static list is the other kind of
 *   segment, chosen on the page before this one.
 * - **No source-scope chips.** Scope is a rule here — `Upload is any of …` or
 *   `Source is …` — so it composes with the rest instead of being a second,
 *   invisible filter beside them.
 * - **No "Rule JSON" panel.** The brief is "JSON-free"; the rules read as
 *   sentences on the saved page (`RuleTreeSummary`).
 *
 * ## SAVING
 *
 * Save is refused while any rule is incomplete. The draft model drops
 * incomplete rules on the way to the API (`toRuleTree`), which is right for a
 * preview and wrong for a save: the rule a person can see would not be the
 * rule that was saved. So the button says what is unfinished instead.
 *
 * The builder is not a `<form>`, and Save is a plain button. Inside a form,
 * Enter in any value box — a city, a number — submits it, and on this screen a
 * submit CREATES a segment. A half-written rule set saved by a keystroke is
 * worse than a form that ignores Enter.
 */
import {
  MAX_SEGMENT_DESCRIPTION_LENGTH,
  createRuleSegmentRequestSchema,
  type FieldDateFormat,
  type RuleTree,
  type Segment,
} from '@wend/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Spinner,
  useToast,
} from '@wend/ui';
import { Check, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import {
  emptyRoot,
  fromRuleTree,
  makeRule,
  nextRuleField,
  previewData,
  useRuleTreeEditor,
  type FieldIndex,
  type PreviewState,
} from '../../components/rules';
import { Textarea } from '../../components/Textarea';
import { isAbortError } from '../../lib/api/abort';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import { updateFieldSettings } from '../../lib/api/fields';
import { previewRules } from '../../lib/api/rules';
import { createRuleSegment, updateSegment } from '../../lib/api/segments';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { pluralise } from '../../lib/format';
import { useFieldCatalogue } from '../../lib/use-field-catalogue';
import { RuleBuilderWorkspace, builderVerdict } from './RuleBuilderWorkspace';

export { DYNAMIC_SENTENCE } from './RuleBuilderWorkspace';

/** Typing settles this long before the counts are asked for again. */
export const PREVIEW_DEBOUNCE_MS = 450;

/* -------------------------------------------------------------------------- */
/* The live preview                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Debounced, abortable counts for a complete tree. `null` means nothing is
 * complete enough to count — the `idle` state, which is not a zero.
 */
export function useRulePreview(tree: RuleTree | null): PreviewState {
  const key = tree === null ? '' : JSON.stringify(tree);
  const [state, setState] = useState<PreviewState>({ status: 'idle' });

  useEffect(() => {
    if (tree === null) {
      setState({ status: 'idle' });
      return undefined;
    }
    setState((previous) => ({ status: 'pending', last: previewData(previous) }));
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      previewRules(tree, controller.signal).then(
        (data) => {
          if (!controller.signal.aborted) setState({ status: 'ready', data });
        },
        (error: unknown) => {
          if (controller.signal.aborted || isAbortError(error)) return;
          setState((previous) => ({ status: 'error', message: errorMessage(error), last: previewData(previous) }));
        },
      );
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
    // `key` is the tree's identity; the object itself is new on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}

/* -------------------------------------------------------------------------- */
/* The container                                                               */
/* -------------------------------------------------------------------------- */

export type RuleSegmentBuilderProps =
  | { readonly mode: 'create' }
  | {
      readonly mode: 'edit';
      readonly segment: Segment;
      readonly onSaved: (segment: Segment) => void;
      readonly onCancel: () => void;
    };

export function RuleSegmentBuilder(props: RuleSegmentBuilderProps) {
  const { index, error, loadingFirst, reload, applyField } = useFieldCatalogue();

  if (index === null) {
    return (
      <Card>
        {loadingFirst ? (
          <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading this workspace’s fields…</span>
          </CardContent>
        ) : (
          <ErrorState error={error} title="Could not load your fields" onRetry={reload} />
        )}
      </Card>
    );
  }

  return <LoadedBuilder {...props} index={index} applyField={applyField} />;
}

function LoadedBuilder(
  props: RuleSegmentBuilderProps & { readonly index: FieldIndex; readonly applyField: ReturnType<typeof useFieldCatalogue>['applyField'] },
) {
  const { index, applyField } = props;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [tree, actions] = useRuleTreeEditor(() => {
    if (props.mode === 'edit' && props.segment.rules !== null) return fromRuleTree(props.segment.rules);
    const first = nextRuleField(index, null);
    const root = emptyRoot();
    return first === null ? root : { ...root, children: [makeRule(first)] };
  }, index);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const verdict = builderVerdict(index, tree);
  const preview = useRulePreview(verdict.blocked === null ? verdict.complete : null);

  async function chooseDateFormat(key: string, format: FieldDateFormat): Promise<void> {
    applyField(await updateFieldSettings(key, { dateFormat: format }));
  }

  async function onSubmit(): Promise<void> {
    if (pending || verdict.saveBlocked !== null || verdict.complete === null) return;
    setRequestId(undefined);

    try {
      if (props.mode === 'create') {
        const result = validate(createRuleSegmentRequestSchema, {
          type: 'rule',
          name,
          description: description.trim() === '' ? null : description,
          rules: verdict.complete,
        });
        if (!result.ok) {
          setErrors(result.errors);
          return;
        }
        setErrors({});
        setPending(true);
        const segment = await createRuleSegment(result.data);
        toast({
          tone: 'success',
          title: 'Segment created',
          description: `${segment.name} holds ${pluralise(segment.counts.uniqueRecipients, 'person', 'people')} now, and updates as data arrives.`,
        });
        navigate(`/segments/${segment.id}`, { replace: true });
      } else {
        setErrors({});
        setPending(true);
        const segment = await updateSegment(props.segment.id, { rules: verdict.complete });
        toast({ tone: 'success', title: 'Rules saved', description: 'Members were brought up to date with the new rules.' });
        setPending(false);
        props.onSaved(segment);
      }
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  return (
    // A div, not a form: Enter in a rule's value box must never create the
    // segment. Saving is the button below, and only the button.
    <div className="flex flex-col gap-6">
      {errors[ROOT_FIELD] !== undefined && (
        <FormAlert tone="error" requestId={requestId}>
          {errors[ROOT_FIELD]}
        </FormAlert>
      )}

      {props.mode === 'create' && (
        <Card>
          <CardHeader>
            <CardTitle>Name this segment</CardTitle>
            <CardDescription>A segment is who you are going to send to. This one is described by rules, and keeps itself current.</CardDescription>
          </CardHeader>
          <CardContent className="flex max-w-xl flex-col gap-5">
            <FormField label="Name" required error={errors.name}>
              <Input
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setErrors((current) => withoutError(current, 'name'));
                }}
                placeholder="Pune & Nashik regulars · 25+"
                autoComplete="off"
                disabled={pending}
              />
            </FormField>
            <FormField
              label="Description"
              description={`Optional. Why this group exists, for whoever opens it next. Up to ${String(MAX_SEGMENT_DESCRIPTION_LENGTH)} characters.`}
              error={errors.description}
            >
              <Textarea
                rows={2}
                value={description}
                maxLength={MAX_SEGMENT_DESCRIPTION_LENGTH}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setErrors((current) => withoutError(current, 'description'));
                }}
                disabled={pending}
              />
            </FormField>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{props.mode === 'create' ? 'Who belongs in it' : 'Edit the rules'}</CardTitle>
          <CardDescription>
            Any column from your uploads can be a rule — pick it from the field list. Each count is the people that rule
            matches on its own.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RuleBuilderWorkspace
            index={index}
            tree={tree}
            actions={actions}
            preview={preview}
            onChooseDateFormat={chooseDateFormat}
            disabled={pending}
          />
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <Button
          type="button"
          loading={pending}
          disabled={verdict.saveBlocked !== null}
          onClick={() => {
            void onSubmit();
          }}
        >
          {props.mode === 'create' ? <Plus aria-hidden="true" className="size-4" /> : <Check aria-hidden="true" className="size-4" />}
          {props.mode === 'create'
            ? pending
              ? 'Creating the segment'
              : 'Create segment'
            : pending
              ? 'Saving the rules'
              : 'Save rules'}
        </Button>
        {props.mode === 'edit' && (
          <Button type="button" variant="secondary" onClick={props.onCancel} disabled={pending}>
            Cancel
          </Button>
        )}
        <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted" aria-live="polite">
          {verdict.saveBlocked ?? (props.mode === 'edit' ? 'Saving brings the members up to date at once, and logs who entered and left.' : '')}
        </p>
      </div>
    </div>
  );
}
